/* ==========================================================================
   The map

   A real street map under the pins: roads, town names, parks, the Thruway.
   The first version had no map under them at all -- a mile grid and some
   dots -- on the reasoning that relative position and a scale bar were
   enough. Oster: "it should look like zillow or google maps. something
   real". He was right; nobody can tell from two dots on grey whether a job is
   across town or the far side of the highway.

   Tiles come from OpenFreeMap (OpenStreetMap data): free, no account, no
   key, no bill. What it learns is which part of the map is being looked at,
   never which home -- pins sit at a postal code's centre, not an address --
   and the pins themselves are drawn here, not sent anywhere.

   The map library is big, so it is loaded only when somebody opens the map,
   never on the way into the app.

   Tap a price to open that job. Where several jobs share a postal code they
   share a point, so they share a tag: "2 jobs", which opens a short list.
   ========================================================================== */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { GeoJSONSource, Map as MapLibreMap, Marker } from 'maplibre-gl';
import type { Cleaner, House } from '../types';

export interface MapPin {
  id: string;
  lat: number;
  lon: number;
  label: string;
  /** Shown on the tag. Usually a price. */
  badge: string;
  initials: string;
  hue: number;
}

interface Props {
  pins: MapPin[];
  /** The person reading, when we know where they are. */
  me: Pick<Cleaner | House, 'lat' | 'lon'> | null;
  /** How far they said they will travel, drawn to scale. */
  radiusMiles?: number;
  onPick: (id: string) => void;
}

const STYLE = 'https://tiles.openfreemap.org/styles/liberty';
/* Monsey, for the rare reader with no position and no pins. */
const FALLBACK: [number, number] = [-74.0736, 41.1163];

/* Where the map was left, so opening a job and coming back does not throw
   away the pan and zoom you just did. Kept per set of pins: a different set
   (new filters, new day) deserves a fresh fit. */
let remembered: { key: string; center: [number, number]; zoom: number } | null = null;

type MapLib = typeof import('maplibre-gl');
type Area = {
  type: 'Feature'; properties: Record<string, never>;
  geometry: { type: 'Polygon'; coordinates: [number, number][][] };
};

export function JobMap({ pins, me, radiusMiles, onPick }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<MapLibreMap | null>(null);
  const lib = useRef<MapLib | null>(null);
  const markers = useRef<Marker[]>([]);
  const pick = useRef(onPick);
  pick.current = onPick;
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  const hasMe = me?.lat != null && me?.lon != null;
  const key = useMemo(() => pins.map((p) => p.id).sort().join(','), [pins]);
  const keyRef = useRef(key);
  keyRef.current = key;
  /* The app refreshes every few seconds and hands over fresh arrays each
     time. Only a real change should redraw. */
  const sig = JSON.stringify([pins, hasMe ? [me!.lat, me!.lon] : null, radiusMiles ?? null]);

  /* ---- the map itself, made once ---- */
  useEffect(() => {
    let gone = false;
    let timer = 0;
    (async () => {
      try {
        const [ml] = await Promise.all([
          import('maplibre-gl'),
          import('maplibre-gl/dist/maplibre-gl.css'),
        ]);
        if (gone || !box.current) return;
        lib.current = ml;

        const first = pins[0];
        const m = new ml.Map({
          container: box.current,
          style: STYLE,
          center: hasMe ? [me!.lon as number, me!.lat as number]
            : first ? [first.lon, first.lat] : FALLBACK,
          zoom: 11,
          /* Credited below in one small line. The library's own box opens
             over the bottom third of a phone-sized map and reopens itself. */
          attributionControl: false,
          /* On a phone the map sits inside a page that scrolls. One finger
             scrolls the page, two move the map -- as Google's own embeds do --
             or the map swallows every attempt to scroll past it. */
          cooperativeGestures: window.matchMedia('(pointer: coarse)').matches,
          dragRotate: false,
          pitchWithRotate: false,
          touchPitch: false,
        });
        m.touchZoomRotate.disableRotation();
        m.addControl(new ml.NavigationControl({ showCompass: false }), 'top-right');
        m.addControl(new ml.ScaleControl({ unit: 'imperial' }), 'bottom-left');

        /* No connection, a blocked tile host: say so and point at the list,
           rather than leave a blank box. Judged on the style, not on every
           tile -- waiting for the whole street map before drawing a single
           price tag made a slow phone look broken. */
        timer = window.setTimeout(() => { if (!gone && !m.isStyleLoaded()) setFailed(true); }, 20000);
        m.on('style.load', () => {
          window.clearTimeout(timer);
          if (gone) return;
          if (!m.getSource('reach')) {
            m.addSource('reach', { type: 'geojson', data: emptyFeature() });
            m.addLayer({
              id: 'reach-fill', type: 'fill', source: 'reach',
              paint: { 'fill-color': '#1D6E7E', 'fill-opacity': 0.07 },
            });
            m.addLayer({
              id: 'reach-line', type: 'line', source: 'reach',
              paint: {
                'line-color': '#1D6E7E', 'line-width': 1.5,
                'line-dasharray': [3, 3], 'line-opacity': 0.7,
              },
            });
          }
          setReady(true);
        });
        m.on('moveend', () => {
          const c = m.getCenter();
          remembered = { key: keyRef.current, center: [c.lng, c.lat], zoom: m.getZoom() };
        });
        map.current = m;
      } catch {
        if (!gone) setFailed(true);
      }
    })();
    return () => {
      gone = true;
      window.clearTimeout(timer);
      markers.current.forEach((x) => x.remove());
      markers.current = [];
      map.current?.remove();
      map.current = null;
    };
  }, []);

  /* ---- pins, you, and your reach ---- */
  useEffect(() => {
    const m = map.current;
    const ml = lib.current;
    if (!ready || !m || !ml) return;

    markers.current.forEach((x) => x.remove());
    markers.current = [];

    /* Same point, same tag. A postal code is as fine as positions go, so
       zooming in would never pull these apart. */
    const groups = new Map<string, MapPin[]>();
    for (const p of pins) {
      const k = p.lat.toFixed(4) + ',' + p.lon.toFixed(4);
      groups.set(k, [...(groups.get(k) ?? []), p]);
    }

    /* You first, so the tags are drawn over you rather than under. */
    if (hasMe) {
      const dot = document.createElement('div');
      dot.className = 'mapme';
      dot.title = 'You';
      markers.current.push(
        new ml.Marker({ element: dot }).setLngLat([me!.lon as number, me!.lat as number]).addTo(m),
      );
    }

    for (const items of groups.values()) {
      const at: [number, number] = [items[0].lon, items[0].lat];
      const tag = document.createElement('button');
      tag.type = 'button';
      if (items.length === 1) {
        const p = items[0];
        tag.className = 'mappin';
        tag.innerHTML = '<b></b><span></span>';
        tag.querySelector('b')!.textContent = p.badge;
        tag.querySelector('span')!.textContent = p.label;
        tag.setAttribute('aria-label', p.label + ', ' + p.badge + ' an hour. Open the job.');
        tag.addEventListener('click', (e) => { e.stopPropagation(); pick.current(p.id); });
      } else {
        tag.className = 'mappin many';
        tag.innerHTML = '<b></b>';
        tag.querySelector('b')!.textContent = items.length + ' jobs';
        tag.setAttribute('aria-label', items.length + ' jobs here. Show them.');
        tag.addEventListener('click', (e) => {
          e.stopPropagation();
          const list = document.createElement('div');
          list.className = 'mappop';
          for (const p of items) {
            const row = document.createElement('button');
            row.type = 'button';
            row.innerHTML = '<span></span><b></b>';
            row.querySelector('span')!.textContent = p.label;
            row.querySelector('b')!.textContent = p.badge;
            row.addEventListener('click', () => pick.current(p.id));
            list.appendChild(row);
          }
          new ml.Popup({ offset: 24, closeButton: false, maxWidth: '240px' })
            .setLngLat(at).setDOMContent(list).addTo(m);
        });
      }
      markers.current.push(new ml.Marker({ element: tag, anchor: 'bottom' }).setLngLat(at).addTo(m));
    }

    (m.getSource('reach') as GeoJSONSource | undefined)?.setData(
      hasMe && radiusMiles ? circle(me!.lat as number, me!.lon as number, radiusMiles) : emptyFeature(),
    );

    /* Back where you left it, or framed so every job and you are in view. */
    if (remembered && remembered.key === key) {
      m.jumpTo({ center: remembered.center, zoom: remembered.zoom });
    } else {
      const pts: [number, number][] = pins.map((p) => [p.lon, p.lat]);
      if (hasMe) pts.push([me!.lon as number, me!.lat as number]);
      if (pts.length) {
        const b = new ml.LngLatBounds(pts[0], pts[0]);
        pts.forEach((p) => b.extend(p));
        m.fitBounds(b, {
          padding: { top: 70, bottom: 40, left: 50, right: 60 }, maxZoom: 13, duration: 0,
        });
      }
    }
  }, [ready, sig]);

  return (
    <div className="mapwrap">
      <div ref={box} className="jobmap" role="region" aria-label={pins.length + ' open jobs on a map'} />
      <a
        className="mapcredit"
        href="https://www.openstreetmap.org/copyright"
        target="_blank"
        rel="noreferrer"
      >
        © OpenStreetMap · OpenFreeMap
      </a>
      {failed && (
        <div className="mapfail">
          <b>The map could not load.</b>
          <span>Check the connection, or switch to the list.</span>
        </div>
      )}
    </div>
  );
}

function emptyFeature(): Area {
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [] } };
}

/** A circle of `miles` around a point, as the polygon a map can draw. */
function circle(lat: number, lon: number, miles: number): Area {
  const dLat = miles / 69.05;
  const dLon = miles / (69.17 * Math.cos((lat * Math.PI) / 180));
  const ring: [number, number][] = [];
  for (let i = 0; i <= 64; i++) {
    const a = (i / 64) * Math.PI * 2;
    ring.push([lon + Math.cos(a) * dLon, lat + Math.sin(a) * dLat]);
  }
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } };
}
